import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/Button';
import { User, Bell, Shield, CreditCard, Save, Loader2, AlertCircle, CheckCircle, Clock, ArrowRight, X, Crown, Star, Zap, Contact, CreditCard as Edit, Trash2, Plus, Download, Smartphone, Check, Globe } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { stripeProducts, getProductByPriceId } from '../stripe-config';
import { useNavigate } from 'react-router-dom';
import { APIKeyManagement } from '../components/APIKeyManagement';
import ProfileCardForm from '../components/ProfileCardForm';
import { profileCardService, ProfileCard, ProfileCardData } from '../lib/profileCardService';
import { useTranslation, LANGUAGES, type Language } from '../lib/i18n';
import { usePWAInstall } from '../hooks/usePWAInstall';

interface NotificationPreferences {
  comments: boolean;
  documentUpdates: boolean;
}

interface Subscription {
  customer_id: string;
  subscription_id: string;
  subscription_status: string;
  price_id: string;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

interface BillingHistory {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
}

const Settings = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useTranslation();
  const { canInstall, installed, promptInstall, isIOS } = usePWAInstall();
  const [fullName, setFullName] = useState('');
  const [emailNotifications, setEmailNotifications] = useState<NotificationPreferences>({
    comments: false,
    documentUpdates: false
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [resetCooldown, setResetCooldown] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [showBillingHistory, setShowBillingHistory] = useState(false);

  const [profileCards, setProfileCards] = useState<ProfileCard[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [editingCard, setEditingCard] = useState<ProfileCard | null>(null);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('users')
          .select('full_name, notification_preferences')
          .eq('id', user.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setFullName(data.full_name || '');
          if (data.notification_preferences) {
            setEmailNotifications(data.notification_preferences);
          }
        } else {
          const { error: createError } = await supabase
            .from('users')
            .insert([
              {
                id: user.id,
                email: user.email,
                full_name: '',
                notification_preferences: {
                  comments: false,
                  documentUpdates: false
                }
              }
            ]);

          if (createError) throw createError;
        }
      } catch (error: any) {
        console.error('Error loading user profile:', error);
        setMessage({ 
          text: t('settings.failedLoadProfile', { error: error.message || t('settings.unknownError') }), 
          type: 'error' 
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [user?.id]);

  // Load subscription data
  useEffect(() => {
    const loadSubscriptionData = async () => {
      if (!user?.id) return;

      try {
        setLoadingBilling(true);

        console.log('⚙️ SETTINGS: Loading subscription data for user:', user.id);

        // Get user's customer record first
        const { data: customerData, error: customerError } = await supabase
          .from('stripe_customers')
          .select('customer_id')
          .eq('user_id', user.id)
          .maybeSingle();

        console.log('⚙️ SETTINGS: Customer data:', customerData);
        console.log('⚙️ SETTINGS: Customer error:', customerError);

        if (customerError || !customerData?.customer_id) {
          console.log('⚙️ SETTINGS: No customer found, using free plan');
          setSubscription(null);
        } else {
          // Get subscription for this customer
          const { data: subData, error: subError } = await supabase
            .from('stripe_subscriptions')
            .select('*')
            .eq('customer_id', customerData.customer_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          console.log('⚙️ SETTINGS: Subscription data:', subData);
          console.log('⚙️ SETTINGS: Subscription error:', subError);

          if (subError || !subData) {
            console.log('⚙️ SETTINGS: No subscription found');
            setSubscription(null);
          } else {
            // Map to expected format
            const mappedData = {
              customer_id: subData.customer_id,
              subscription_id: subData.subscription_id,
              subscription_status: subData.status,
              price_id: subData.price_id,
              current_period_start: subData.current_period_start,
              current_period_end: subData.current_period_end,
              cancel_at_period_end: subData.cancel_at_period_end,
              payment_method_brand: subData.payment_method_brand,
              payment_method_last4: subData.payment_method_last4
            };
            console.log('⚙️ SETTINGS: Mapped subscription:', mappedData);
            setSubscription(mappedData);
          }
        }

        // Load billing history from orders view
        const { data: historyData, error: historyError } = await supabase
          .from('stripe_user_orders')
          .select('*')
          .order('order_date', { ascending: false })
          .limit(5);

        if (historyError) {
          console.error('Error loading billing history:', historyError);
          setBillingHistory([]);
        } else {
          // Transform order data to billing history format
          const transformedHistory = historyData?.map(order => ({
            id: order.order_id.toString(),
            amount: order.amount_total,
            currency: order.currency,
            status: order.payment_status,
            created_at: order.order_date
          })) || [];
          setBillingHistory(transformedHistory);
        }
      } catch (error: any) {
        console.error('Error loading billing data:', error);
        setMessage({
          text: t('settings.failedLoadBilling', { error: error.message || t('settings.unknownError') }),
          type: 'error'
        });
      } finally {
        setLoadingBilling(false);
      }
    };

    loadSubscriptionData();
  }, [user?.id]);

  useEffect(() => {
    const loadProfileCards = async () => {
      if (!user?.id) {
        console.warn('Settings: No user ID available for loading profile cards', { user });
        return;
      }

      try {
        setLoadingCards(true);
        console.log('Settings: Loading profile cards for user:', user.id);
        const { data, error } = await profileCardService.getAll(user.id);

        if (error) {
          console.error('Settings: Error loading profile cards:', error);
          throw error;
        }

        console.log('Settings: Loaded profile cards:', data);
        setProfileCards(data || []);
      } catch (error: any) {
        console.error('Settings: Failed to load profile cards:', error);
        setMessage({
          text: t('settings.failedLoadCards', { error: error.message || t('settings.unknownError') }),
          type: 'error'
        });
      } finally {
        setLoadingCards(false);
      }
    };

    loadProfileCards();
  }, [user?.id]);

  useEffect(() => {
    let timer: number;
    if (resetCooldown > 0) {
      timer = window.setInterval(() => {
        setResetCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resetCooldown]);

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('id', user.id)
        .single();

      if (checkError) throw checkError;

      const updateData = {
        full_name: fullName,
        notification_preferences: emailNotifications
      };

      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user.id);

      if (updateError) throw updateError;

      setMessage({ 
        text: t('settings.profileUpdated'), 
        type: 'success' 
      });

      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setMessage({ 
        text: error.message || t('settings.failedUpdateProfile'), 
        type: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (resetCooldown > 0) {
      setMessage({ 
        text: t('settings.waitResetRequest', { seconds: resetCooldown }), 
        type: 'error'
      });
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user?.email || '', {
        redirectTo: `${window.location.origin}/settings`,
      });

      if (error) {
        if (error.message.includes('rate_limit')) {
          setResetCooldown(60);
          throw new Error(t('settings.waitResetRequest', { seconds: 60 }));
        }
        throw error;
      }

      setMessage({ 
        text: t('settings.passwordResetSent'), 
        type: 'success' 
      });
      setResetCooldown(60);
    } catch (error: any) {
      setMessage({ text: error.message, type: 'error' });
    }
  };

  const handleNotificationChange = async (key: keyof NotificationPreferences) => {
    if (!user?.id) return;

    const newSettings = {
      ...emailNotifications,
      [key]: !emailNotifications[key]
    };

    try {
      const { error } = await supabase
        .from('users')
        .update({
          notification_preferences: newSettings
        })
        .eq('id', user.id);

      if (error) throw error;

      setEmailNotifications(newSettings);
      setMessage({ 
        text: t('settings.notificationsSaved'), 
        type: 'success' 
      });

      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
    } catch (error: any) {
      console.error('Error saving notification preferences:', error);
      setMessage({ 
        text: error.message || t('settings.failedSaveNotifications'), 
        type: 'error' 
      });
    }
  };

  const handleCancelSubscription = async () => {
    if (!user?.id || !subscription?.subscription_id) return;

    const confirmed = window.confirm(t('settings.confirmCancelSub'));
    if (!confirmed) return;

    try {
      // This would typically call a Stripe webhook or API to cancel the subscription
      // For now, we'll just update the local state
      setSubscription(prev => prev ? {
        ...prev,
        cancel_at_period_end: true
      } : null);

      setMessage({
        text: t('settings.subscriptionCanceled'),
        type: 'success'
      });
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      setMessage({
        text: error.message || t('settings.failedCancelSub'),
        type: 'error'
      });
    }
  };

  const handleUpgrade = (priceId: string) => {
    navigate(`/pricing?selected=${priceId}`);
  };

  const handleSaveCard = async (cardName: string, profileData: ProfileCardData, isDefault: boolean) => {
    if (!user?.id) return;

    try {
      if (editingCard) {
        const { data, error } = await profileCardService.update(editingCard.id, user.id, {
          card_name: cardName,
          profile_data: profileData,
          is_default: isDefault,
        });

        if (error) throw error;

        setProfileCards(prev =>
          prev.map(card => (card.id === editingCard.id ? data! : card))
        );

        setMessage({
          text: t('settings.cardUpdated'),
          type: 'success'
        });
      } else {
        const { data, error } = await profileCardService.create(user.id, {
          card_name: cardName,
          profile_data: profileData,
          is_default: isDefault,
        });

        if (error) throw error;

        setProfileCards(prev => [data!, ...prev]);

        setMessage({
          text: t('settings.cardCreated'),
          type: 'success'
        });
      }

      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
    } catch (error: any) {
      console.error('Error saving profile card:', error);
      throw error;
    }
  };

  const handleEditCard = (card: ProfileCard) => {
    setEditingCard(card);
    setShowCardForm(true);
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!user?.id) return;

    const confirmed = window.confirm(t('settings.confirmDeleteCard'));
    if (!confirmed) return;

    try {
      const { error } = await profileCardService.delete(cardId, user.id);

      if (error) throw error;

      setProfileCards(prev => prev.filter(card => card.id !== cardId));

      setMessage({
        text: t('settings.cardDeleted'),
        type: 'success'
      });

      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
    } catch (error: any) {
      console.error('Error deleting profile card:', error);
      setMessage({
        text: error.message || t('settings.failedDeleteCard'),
        type: 'error'
      });
    }
  };

  const handleCloseCardForm = () => {
    setShowCardForm(false);
    setEditingCard(null);
  };

  const getCurrentPlan = () => {
    if (!subscription || subscription.subscription_status === 'not_started' || !subscription.price_id) {
      return {
        name: t('settings.freePlan'),
        price: 0,
        features: [t('settings.feat.free1'), t('settings.feat.free2'), t('settings.feat.free3')],
        icon: <Star className="w-6 h-6 text-gray-500" />
      };
    }

    const product = getProductByPriceId(subscription.price_id);
    if (!product) {
      return {
        name: t('settings.unknownPlan'),
        price: 0,
        features: [t('settings.contactSupportDetails')],
        icon: <AlertCircle className="w-6 h-6 text-gray-500" />
      };
    }

    let features: string[] = [];
    let icon = <Zap className="w-6 h-6 text-blue-500" />;

    if (product.name.includes('Basic')) {
      features = [
        t('settings.feat.basic1'),
        t('settings.feat.basic2'),
        t('settings.feat.basic3'),
        t('settings.feat.basic4'),
        t('settings.feat.basic5'),
        t('settings.feat.basic6')
      ];
      icon = <Star className="w-6 h-6 text-blue-500" />;
    } else if (product.name.includes('Team')) {
      features = [
        t('settings.feat.team1'),
        t('settings.feat.team2'),
        t('settings.feat.team3'),
        t('settings.feat.team4'),
        t('settings.feat.team5'),
        t('settings.feat.team6'),
        t('settings.feat.team7'),
        t('settings.feat.team8')
      ];
      icon = <Crown className="w-6 h-6 text-purple-500" />;
    } else if (product.name.includes('AI')) {
      features = [
        t('settings.feat.ai1'),
        t('settings.feat.ai2'),
        t('settings.feat.ai3'),
        t('settings.feat.ai4'),
        t('settings.feat.ai5'),
        t('settings.feat.ai6'),
        t('settings.feat.ai7'),
        t('settings.feat.ai8')
      ];
      icon = <Zap className="w-6 h-6 text-green-500" />;
    } else {
      features = [
        t('settings.feat.ai1'),
        t('settings.feat.ai2'),
        t('settings.feat.ai3'),
        t('settings.feat.ai4'),
        t('settings.feat.ai5'),
        t('settings.feat.ai6'),
        t('settings.feat.ai7'),
        t('settings.feat.ai8')
      ];
      icon = <Zap className="w-6 h-6 text-green-500" />;
    }

    return {
      name: product.name,
      price: product.price,
      features,
      icon
    };
  };

  const getAvailablePlans = () => {
    const currentPlan = getCurrentPlan();
    return stripeProducts.filter(product => product.name !== currentPlan.name);
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-500 dark:text-gray-400">{t('settings.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  const currentPlan = getCurrentPlan();
  const availablePlans = getAvailablePlans();

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <h1 className="text-3xl font-bold">{t('settings.title')}</h1>

      {message.text && (
        <div className={`flex items-center gap-2 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' 
            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          {message.text}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md divide-y divide-gray-200 dark:divide-gray-700">
        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
              <User className="w-6 h-6 text-blue-500 dark:text-blue-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{t('settings.profileSettings')}</h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('settings.profileSettingsDesc')}
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.emailLabel')}</label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.fullName')}</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('settings.enterFullName')}
                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              />
            </div>
            <Button 
              onClick={handleSaveProfile} 
              disabled={saving}
              className="w-full sm:w-auto"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('settings.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('settings.saveChanges')}
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-teal-100 dark:bg-teal-900 p-3 rounded-full">
              <Globe className="w-6 h-6 text-teal-500 dark:text-teal-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{t('settings.language')}</h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('settings.languageDesc')}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code as Language)}
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                  language === lang.code
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="flex items-center">
                  <span className="text-2xl mr-3">{lang.flag}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {lang.label}
                  </span>
                </span>
                {language === lang.code && (
                  <Check className="w-5 h-5 text-blue-500" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full">
              <Bell className="w-6 h-6 text-purple-500 dark:text-purple-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{t('settings.notifications')}</h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('settings.notificationsDesc')}
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={emailNotifications.documentUpdates}
                onChange={() => handleNotificationChange('documentUpdates')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700 dark:text-gray-300">
                {t('settings.docUpdateNotifications')}
              </span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={emailNotifications.comments}
                onChange={() => handleNotificationChange('comments')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700 dark:text-gray-300">
                {t('settings.commentNotifications')}
              </span>
            </label>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-green-100 dark:bg-green-900 p-3 rounded-full">
              <Shield className="w-6 h-6 text-green-500 dark:text-green-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{t('settings.security')}</h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('settings.securityDesc')}
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('settings.securityMessage')}
            </p>
            <Button 
              variant="outline" 
              onClick={handleChangePassword}
              disabled={resetCooldown > 0}
              className="w-full sm:w-auto"
            >
              <Shield className="w-4 h-4 mr-2" />
              {resetCooldown > 0 
                ? t('settings.waitResetPassword', { seconds: resetCooldown })
                : t('settings.changePassword')
              }
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
              <Smartphone className="w-6 h-6 text-blue-500 dark:text-blue-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{t('pwa.settingsTitle')}</h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('pwa.settingsDesc')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {installed ? (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm text-green-700 dark:text-green-300">{t('pwa.settingsInstalled')}</span>
              </div>
            ) : canInstall ? (
              <Button onClick={promptInstall} className="w-full sm:w-auto">
                <Download className="w-4 h-4 mr-2" />
                {t('pwa.settingsInstall')}
              </Button>
            ) : isIOS ? (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <Smartphone className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-blue-700 dark:text-blue-300">{t('pwa.settingsIOS')}</span>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                <Clock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('pwa.settingsDesc')}</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-teal-100 dark:bg-teal-900 p-3 rounded-full">
              <Contact className="w-6 h-6 text-teal-500 dark:text-teal-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{t('settings.personalCards')}</h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('settings.personalCardsDesc')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Button
              onClick={() => {
                setEditingCard(null);
                setShowCardForm(true);
              }}
              className="w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('settings.addNewCard')}
            </Button>

            {loadingCards ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : profileCards.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <Contact className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  {t('settings.noProfileCards')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.noProfileCardsDesc')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profileCards.map((card) => (
                  <div
                    key={card.id}
                    className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {card.card_name}
                        </h3>
                        {card.is_default && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditCard(card)}
                          className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          title={t('settings.editCard')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCard(card.id)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title={t('settings.deleteCard')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                      {card.profile_data.full_name && (
                        <p className="flex items-start">
                          <span className="font-medium min-w-[80px]">{t('settings.name')}</span>
                          <span className="flex-1">{card.profile_data.full_name}</span>
                        </p>
                      )}
                      {card.profile_data.email && (
                        <p className="flex items-start">
                          <span className="font-medium min-w-[80px]">{t('settings.emailLabel')}</span>
                          <span className="flex-1">{card.profile_data.email}</span>
                        </p>
                      )}
                      {card.profile_data.phone_number && (
                        <p className="flex items-start">
                          <span className="font-medium min-w-[80px]">{t('settings.phone')}</span>
                          <span className="flex-1">{card.profile_data.phone_number}</span>
                        </p>
                      )}
                      {card.profile_data.company_name && (
                        <p className="flex items-start">
                          <span className="font-medium min-w-[80px]">{t('settings.company')}</span>
                          <span className="flex-1">{card.profile_data.company_name}</span>
                        </p>
                      )}
                      {card.profile_data.city && (
                        <p className="flex items-start">
                          <span className="font-medium min-w-[80px]">{t('settings.location')}</span>
                          <span className="flex-1">
                            {[card.profile_data.city, card.profile_data.state, card.profile_data.country]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <APIKeyManagement />

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-full">
              <CreditCard className="w-6 h-6 text-orange-500 dark:text-orange-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{t('settings.billingSubscription')}</h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('settings.billingDesc')}
              </p>
            </div>
          </div>
          
          <div className="space-y-6">
            {loadingBilling ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : (
              <>
                {/* Current Plan */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {currentPlan.icon}
                      <div>
                        <h3 className="font-semibold text-lg">{currentPlan.name}</h3>
                        <p className="text-2xl font-bold">
                          ${currentPlan.price}
                          <span className="text-sm font-normal text-gray-500">{t('settings.perMonth')}</span>
                        </p>
                      </div>
                    </div>
                    {subscription && subscription.current_period_end && (
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {subscription.cancel_at_period_end
                            ? t('settings.expiresOn', { date: format(new Date(subscription.current_period_end! * 1000), 'MMMM d, yyyy') })
                            : t('settings.renewsOn', { date: format(new Date(subscription.current_period_end! * 1000), 'MMMM d, yyyy') })
                          }
                        </p>
                        <p className="font-medium">
                          {format(new Date(subscription.current_period_end * 1000), 'MMM d, yyyy')}
                        </p>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2 mb-4">
                    {currentPlan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {subscription && subscription.subscription_status !== 'not_started' ? (
                    subscription.cancel_at_period_end ? (
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('settings.subEndsOn', { date: format(new Date(subscription.current_period_end! * 1000), 'MMMM d, yyyy') })}
                      </p>
                    ) : (
                      <Button 
                        variant="outline" 
                        onClick={handleCancelSubscription}
                        className="w-full sm:w-auto"
                      >
                        {t('settings.cancelSubscription')}
                      </Button>
                    )
                  ) : null}
                </div>

                {/* Available Upgrades */}
                {availablePlans.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-4">{t('settings.availablePlans')}</h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {availablePlans.map((plan) => {
                        const planFeatures = plan.name.includes('Basic') ? [
                          t('settings.feat.basic1'),
                          t('settings.feat.basic2'),
                          t('settings.feat.basic3'),
                          t('settings.feat.basic4'),
                          t('settings.feat.basic5'),
                          t('settings.feat.basic6')
                        ] : plan.name.includes('Team') ? [
                          t('settings.feat.team1'),
                          t('settings.feat.team2'),
                          t('settings.feat.team3'),
                          t('settings.feat.team4'),
                          t('settings.feat.team5'),
                          t('settings.feat.team6')
                        ] : [
                          t('settings.feat.ai1'),
                          t('settings.feat.ai2'),
                          t('settings.feat.ai3'),
                          t('settings.feat.ai4'),
                          t('settings.feat.ai5'),
                          t('settings.feat.ai6')
                        ];

                        const planIcon = plan.name.includes('Basic') ? (
                          <Star className="w-6 h-6 text-blue-500" />
                        ) : plan.name.includes('Team') ? (
                          <Crown className="w-6 h-6 text-purple-500" />
                        ) : (
                          <Zap className="w-6 h-6 text-green-500" />
                        );

                        return (
                          <div 
                            key={plan.id}
                            className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
                          >
                            <div className="flex items-center space-x-3 mb-3">
                              {planIcon}
                              <div>
                                <h4 className="font-semibold">{plan.name}</h4>
                                <p className="text-lg font-bold">
                                  ${plan.price}
                                  <span className="text-sm font-normal text-gray-500">{t('settings.perMonth')}</span>
                                </p>
                              </div>
                            </div>
                            <ul className="text-sm space-y-1 mb-4">
                              {planFeatures.slice(0, 3).map((feature, index) => (
                                <li key={index} className="flex items-center">
                                  <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                                  {feature}
                                </li>
                              ))}
                              {planFeatures.length > 3 && (
                                <li className="text-gray-500 text-xs">
                                  {t('settings.moreFeatures', { count: planFeatures.length - 3 })}
                                </li>
                              )}
                            </ul>
                            <Button 
                              onClick={() => handleUpgrade(plan.priceId)}
                              className="w-full"
                              size="sm"
                            >
                              {t('settings.upgradeTo', { plan: plan.name })}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Billing History */}
                {billingHistory.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">{t('settings.billingHistory')}</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBillingHistory(!showBillingHistory)}
                      >
                        {showBillingHistory ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <ArrowRight className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {showBillingHistory && (
                      <div className="space-y-2">
                        {billingHistory.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                          >
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 text-gray-400 mr-3" />
                              <div>
                                <p className="font-medium">
                                  ${(item.amount / 100).toFixed(2)} {item.currency.toUpperCase()}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {format(new Date(item.created_at), 'MMM d, yyyy')}
                                </p>
                              </div>
                            </div>
                            <span className={`text-sm px-2 py-1 rounded ${
                              item.status === 'succeeded'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                : item.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                            }`}>
                              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ProfileCardForm
        isOpen={showCardForm}
        onClose={handleCloseCardForm}
        onSave={handleSaveCard}
        editingCard={editingCard}
        existingCardNames={profileCards.map(card => card.card_name)}
      />
    </div>
  );
};

export default Settings;